/**
 * Post-game results and analytics page.
 * Shows final standings, per-question difficulty, and round breakdowns.
 */
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Box,
  Flex,
  Card,
  Table,
  Heading,
  Text,
  Badge,
  Button,
  Progress,
} from '@radix-ui/themes';
import api from '../../utils/api';
import { mapRoundAnalysis, type RoundAnalysis } from './roundAnalysis';

interface Standing {
  teamId: string;
  teamName: string;
  playerCount: number;
  roundScores: Array<{
    roundIndex: number;
    questionPoints: number;
    bonusPoints: number;
    subtotal: number;
    isDoubled: boolean;
    total: number;
  }>;
  grandTotal: number;
  rank: number;
}

interface EventData {
  id: string;
  date: string;
  pin: string;
  status: string;
  venue: { name: string };
  quiz: { name: string };
}

const EventResults: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [event, setEvent] = useState<EventData | null>(null);
  const [standings, setStandings] = useState<Standing[]>([]);
  const [analysis, setAnalysis] = useState<RoundAnalysis[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [evRes, standRes, analysisRes] = await Promise.all([
        api.get(`/api/events/${id}`),
        api.get(`/api/scores/event/${id}/standings`),
        api.get(`/api/scores/event/${id}/analysis`),
      ]);
      setEvent(evRes.data.data?.event ?? evRes.data);
      setStandings(standRes.data.data?.standings ?? []);
      setAnalysis(mapRoundAnalysis(analysisRes.data));
    } catch (err) {
      console.error('Failed to load results', err);
    } finally {
      setLoading(false);
    }
  };

  const rankColor = (rank: number): 'gold' | 'gray' | 'bronze' | undefined => {
    if (rank === 1) return 'gold';
    if (rank === 2) return 'gray';
    if (rank === 3) return 'bronze';
    return undefined;
  };

  const difficultyColor = (pct: number): 'green' | 'amber' | 'orange' | 'red' => {
    if (pct >= 75) return 'green'; // easy
    if (pct >= 50) return 'amber'; // medium
    if (pct >= 25) return 'orange'; // hard
    return 'red'; // very hard
  };

  if (loading)
    return (
      <Box p="6">
        <Text size="2" color="gray">Loading results...</Text>
      </Box>
    );
  if (!event)
    return (
      <Box p="6">
        <Text size="2" color="gray">Event not found</Text>
      </Box>
    );

  // Derive round numbers dynamically from standings data
  const firstTeam = standings[0];
  const roundIndices = firstTeam ? firstTeam.roundScores.map((rs) => rs.roundIndex) : [];

  return (
    <Box p="6">
      <Flex justify="between" align="start" gap="3" wrap="wrap" mb="5">
        <Box>
          <Heading as="h1" size="6">{event.quiz?.name ?? 'Results'}</Heading>
          <Text size="2" color="gray">
            {event.venue?.name} — {new Date(event.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
          </Text>
        </Box>
        <Button asChild variant="soft" color="gray">
          <Link to="/live-events">Back to Events</Link>
        </Button>
      </Flex>

      {/* Final Standings */}
      <Box mb="6">
        <Heading as="h2" size="5" mb="3">Final Standings</Heading>
        <Table.Root variant="surface">
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeaderCell>Rank</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Team</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Players</Table.ColumnHeaderCell>
              {roundIndices.map((r: number) => (
                <Table.ColumnHeaderCell key={r}>R{r}</Table.ColumnHeaderCell>
              ))}
              <Table.ColumnHeaderCell>Bonus</Table.ColumnHeaderCell>
              <Table.ColumnHeaderCell>Total</Table.ColumnHeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {standings.map((team) => {
              // Sum bonus points across all rounds
              const totalBonus = team.roundScores.reduce(
                (sum: number, rs: { bonusPoints: number }) => sum + (rs.bonusPoints ?? 0),
                0
              );
              const color = rankColor(team.rank);
              return (
                <Table.Row key={team.teamId}>
                  <Table.Cell>
                    {color ? (
                      <Badge color={color} variant="soft">#{team.rank}</Badge>
                    ) : (
                      <Text color="gray">#{team.rank}</Text>
                    )}
                  </Table.Cell>
                  <Table.RowHeaderCell>{team.teamName}</Table.RowHeaderCell>
                  <Table.Cell>{team.playerCount}</Table.Cell>
                  {team.roundScores.map((rs) => (
                    <Table.Cell key={rs.roundIndex}>
                      {rs.isDoubled ? (
                        <Text weight="bold">{rs.total}×2</Text>
                      ) : (
                        rs.total
                      )}
                    </Table.Cell>
                  ))}
                  <Table.Cell>{totalBonus}</Table.Cell>
                  <Table.Cell>
                    <Text weight="bold">{team.grandTotal}</Text>
                  </Table.Cell>
                </Table.Row>
              );
            })}
          </Table.Body>
        </Table.Root>
      </Box>

      {/* Question Difficulty Analysis */}
      <Box>
        <Heading as="h2" size="5" mb="3">Question Difficulty</Heading>
        <Flex direction="column" gap="4">
          {analysis.map((round) => (
            <Card key={round.roundNumber}>
              <Heading as="h3" size="4" mb="3">Round {round.roundNumber}{round.title ? `: ${round.title}` : ''}</Heading>
              <Flex direction="column" gap="4">
                {round.questions.map((q, i) => (
                  <Box key={q.questionId}>
                    <Flex gap="2" align="baseline" mb="1">
                      <Text size="2" weight="bold">Q{i + 1}</Text>
                      <Text size="2">{q.text}</Text>
                    </Flex>
                    <Flex align="center" gap="3" mb="1">
                      <Progress
                        value={q.percentCorrect}
                        color={difficultyColor(q.percentCorrect)}
                        size="2"
                        style={{ flex: 1 }}
                      />
                      <Text size="2" color="gray">{Math.round(q.percentCorrect)}%</Text>
                    </Flex>
                    <Text size="1" color="gray">
                      {q.answer} ({q.correctCount}/{q.totalTeams} teams)
                    </Text>
                  </Box>
                ))}
              </Flex>
            </Card>
          ))}
        </Flex>
      </Box>
    </Box>
  );
};

export default EventResults;
